import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Search, Download, Mail, Users, Globe2, TrendingUp, FileText, Filter, X, Copy, Check, FileDown, ShieldCheck, Clock, Sparkles, Send, Megaphone, AlertTriangle, RefreshCw, Eye, Ban, CalendarClock,
} from "lucide-react";
import {
  adminFetchLeads, adminFetchDownloadLeads, adminFetchNewsletterSubscribers,
  adminFetchCampaigns, adminGenerateCampaign, adminApproveCampaign, adminCancelCampaign, adminUnscheduleCampaign,
  isAdminAuthed,
} from "@/lib/blogApi";

const fmtDate = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
};

const fmtDay = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return iso; }
};

const csvEscape = (v) => {
  const s = v == null ? "" : String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const buildBlogCsv = (rows) => {
  const headers = ["created_at", "email", "name", "company", "country", "source_slug", "source_title"];
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvEscape(r[h])).join(",")));
  return lines.join("\n");
};

const buildDownloadCsv = (rows) => {
  const headers = ["createdAt", "email", "full_name", "mobile", "company", "intent", "locale", "status", "verifiedAt", "lastAsset", "requestCount", "consent", "consentAt"];
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvEscape(r[h])).join(",")));
  return lines.join("\n");
};

const buildSubscribersCsv = (rows) => {
  const headers = ["createdAt", "email", "name", "locale", "source", "status", "consent", "consentAt"];
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(headers.map((h) => csvEscape(r[h])).join(",")));
  return lines.join("\n");
};

const AdminLeads = () => {
  const authed = isAdminAuthed();
  const [tab, setTab] = useState("downloads"); // 'downloads' (new) | 'articles' (legacy)

  if (!authed) return <Navigate to="/admin" replace />;

  return (
    <div className="pt-[72px] bg-brand-cream/40 min-h-screen" data-testid="page-admin-leads">
      <div className="container-wide py-10">
        <Link to="/admin/dashboard" className="inline-flex items-center gap-2 text-sm text-brand-black/65 hover:text-brand-teal mb-6">
          <ArrowLeft size={14} /> Back to Dashboard
        </Link>

        <div className="mb-8">
          <p className="eyebrow mb-2">◦  B2B Leads</p>
          <h1 className="font-display text-4xl text-brand-black">Lead Inbox</h1>
          <p className="text-sm text-brand-black/60 mt-2 max-w-xl">
            Buyers who unlocked documents from the SPIRUVA pre-launch library — by submitting their business email, company, intent and GDPR consent. Resource downloads are email-verified before delivery.
          </p>
        </div>

        {/* Tabs */}
        <div className="border-b border-brand-green/20 mb-8 flex gap-1" role="tablist">
          <button
            role="tab"
            aria-selected={tab === "downloads"}
            onClick={() => setTab("downloads")}
            data-testid="leads-tab-downloads"
            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 -mb-px transition ${
              tab === "downloads"
                ? "border-brand-gold text-brand-black"
                : "border-transparent text-brand-black/50 hover:text-brand-black"
            }`}
          >
            Resource Downloads
          </button>
          <button
            role="tab"
            aria-selected={tab === "articles"}
            onClick={() => setTab("articles")}
            data-testid="leads-tab-articles"
            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 -mb-px transition ${
              tab === "articles"
                ? "border-brand-gold text-brand-black"
                : "border-transparent text-brand-black/50 hover:text-brand-black"
            }`}
          >
            Article (Blog) Leads
          </button>
          <button
            role="tab"
            aria-selected={tab === "newsletter"}
            onClick={() => setTab("newsletter")}
            data-testid="leads-tab-newsletter"
            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 -mb-px transition ${
              tab === "newsletter"
                ? "border-brand-gold text-brand-black"
                : "border-transparent text-brand-black/50 hover:text-brand-black"
            }`}
          >
            Newsletter Subscribers
          </button>
          <button
            role="tab"
            aria-selected={tab === "campaigns"}
            onClick={() => setTab("campaigns")}
            data-testid="leads-tab-campaigns"
            className={`px-5 py-3 text-xs font-mono uppercase tracking-[0.2em] border-b-2 -mb-px transition ${
              tab === "campaigns"
                ? "border-brand-gold text-brand-black"
                : "border-transparent text-brand-black/50 hover:text-brand-black"
            }`}
          >
            Campaigns
          </button>
        </div>

        {tab === "downloads" ? <DownloadLeadsPanel /> : tab === "articles" ? <BlogLeadsPanel /> : tab === "newsletter" ? <NewsletterPanel /> : <CampaignsPanel />}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Resource downloads panel — new lead-gate / verification flow
// ----------------------------------------------------------------------------
const STATUS_LABEL = {
  pending: { label: "Pending verify", className: "border-brand-gold/40 text-[#8a6e1f] bg-brand-gold/10", Icon: Clock },
  verified: { label: "Verified", className: "border-brand-green/40 text-brand-green bg-brand-green/10", Icon: ShieldCheck },
};

const INTENT_LABEL = {
  evaluation: "Technical eval",
  procurement: "Procurement",
  rd: "R&D / formulation",
  distribution: "Distribution",
  other: "Other",
};

const DownloadLeadsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [recent, setRecent] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [intentFilter, setIntentFilter] = useState("All");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminFetchDownloadLeads()
      .then((data) => {
        if (!alive) return;
        setLeads(data?.leads || []);
        setRecent(data?.recent_downloads || []);
      })
      .catch(() => toast.error("Could not load resource download leads"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const stats = useMemo(() => {
    const total = leads.length;
    const verified = leads.filter((l) => l.status === "verified").length;
    const pending = total - verified;
    const now = Date.now();
    const last7 = leads.filter((l) => {
      try { return now - new Date(l.lastRequestedAt || l.createdAt).getTime() < 7 * 86400000; } catch { return false; }
    }).length;
    return { total, verified, pending, last7 };
  }, [leads]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter((l) => {
      if (statusFilter !== "All" && (l.status || "pending") !== statusFilter) return false;
      if (intentFilter !== "All" && l.intent !== intentFilter) return false;
      if (!term) return true;
      return (
        (l.email || "").toLowerCase().includes(term) ||
        (l.full_name || "").toLowerCase().includes(term) ||
        (l.company || "").toLowerCase().includes(term) ||
        (l.lastAsset || "").toLowerCase().includes(term)
      );
    }).sort((a, b) => {
      const ta = new Date(a.lastRequestedAt || a.createdAt || 0).getTime();
      const tb = new Date(b.lastRequestedAt || b.createdAt || 0).getTime();
      return tb - ta;
    });
  }, [leads, search, statusFilter, intentFilter]);

  const exportCsv = () => {
    const csv = buildDownloadCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spiruva-download-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}`);
  };

  const copyEmail = async (id, email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      toast.error("Could not copy email");
    }
  };

  const intents = ["All", "evaluation", "procurement", "rd", "distribution", "other"];

  return (
    <div data-testid="admin-download-leads-panel">
      <div className="flex justify-end mb-4">
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="download-leads-export-csv"
          className="btn-primary disabled:opacity-50"
        >
          <Download size={14} /> Export CSV ({filtered.length})
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
        {[
          { icon: Users, l: "Total leads", v: stats.total },
          { icon: ShieldCheck, l: "Verified", v: stats.verified },
          { icon: Clock, l: "Pending verify", v: stats.pending },
          { icon: TrendingUp, l: "Last 7 days", v: stats.last7 },
        ].map((s) => (
          <div key={s.l} className="bg-white p-6">
            <s.icon size={14} className="text-brand-teal" />
            <p className="font-display text-3xl text-brand-black mt-3">{s.v}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-brand-green/15 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search size={14} className="text-brand-black/40 shrink-0" />
          <input
            type="text"
            placeholder="Search name, email, company or asset…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="download-leads-search"
            className="flex-1 bg-transparent text-sm focus:outline-none min-w-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-brand-black/40" />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="download-leads-filter-status"
            className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {["All", "pending", "verified"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select
            value={intentFilter}
            onChange={(e) => setIntentFilter(e.target.value)}
            data-testid="download-leads-filter-intent"
            className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {intents.map((i) => <option key={i} value={i}>{i}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-brand-green/15 overflow-x-auto mb-12">
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Contact</th>
              <th>Company / Intent</th>
              <th>Asset</th>
              <th>Locale</th>
              <th>Status</th>
              <th>Consent</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-brand-black/50 py-8" data-testid="download-leads-loading">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-brand-black/50 py-10" data-testid="download-leads-empty">
                {leads.length === 0
                  ? "No resource downloads captured yet. Leads will appear here when a visitor submits the DownloadGate form on /resources."
                  : "No leads match your filters."}
              </td></tr>
            ) : filtered.map((l) => {
              const status = STATUS_LABEL[l.status || "pending"] || STATUS_LABEL.pending;
              const StatusIcon = status.Icon;
              return (
                <tr key={l.id || l.email} data-testid={`download-lead-row-${l.email}`}>
                  <td>
                    <p className="text-xs text-brand-black/85">{fmtDay(l.lastRequestedAt || l.createdAt)}</p>
                    <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">
                      {fmtDate(l.lastRequestedAt || l.createdAt).split(", ").pop()}
                    </p>
                  </td>
                  <td>
                    <p className="font-medium text-brand-black text-sm">{l.full_name || "—"}</p>
                    <button
                      onClick={() => copyEmail(l.email, l.email)}
                      className="inline-flex items-center gap-1.5 text-xs text-brand-teal hover:text-brand-green font-mono mt-0.5"
                      title="Copy email"
                    >
                      {l.email}
                      {copiedId === l.email ? <Check size={11} className="text-brand-green" /> : <Copy size={10} className="opacity-50" />}
                    </button>
                    {l.mobile && <p className="text-[11px] text-brand-black/55 mt-0.5">{l.mobile}</p>}
                  </td>
                  <td className="max-w-[200px]">
                    <p className="font-medium text-brand-black text-sm line-clamp-2">{l.company}</p>
                    <p className="text-[11px] text-brand-black/55 mt-0.5">{INTENT_LABEL[l.intent] || l.intent || "—"}</p>
                  </td>
                  <td className="max-w-[200px]">
                    {l.lastAsset ? (
                      <span className="inline-flex items-center gap-1.5 text-xs text-brand-black/80">
                        <FileDown size={12} className="text-brand-teal" />
                        <span className="font-mono">{l.lastAsset}</span>
                      </span>
                    ) : "—"}
                    {l.requestCount > 1 && (
                      <p className="text-[10px] font-mono uppercase tracking-widest text-brand-gold mt-0.5">
                        {l.requestCount} requests
                      </p>
                    )}
                  </td>
                  <td>
                    <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/25 text-brand-green bg-brand-green/5">
                      {(l.locale || "en").toUpperCase()}
                    </span>
                  </td>
                  <td>
                    <span className={`inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${status.className}`}
                      data-testid={`download-lead-status-${l.email}`}>
                      <StatusIcon size={11} /> {status.label}
                    </span>
                    {l.verifiedAt && (
                      <p className="text-[10px] text-brand-black/45 mt-0.5">{fmtDate(l.verifiedAt)}</p>
                    )}
                  </td>
                  <td>
                    {l.consent ? (
                      <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-brand-green">
                        <Check size={11} /> Yes
                      </span>
                    ) : "—"}
                    {l.consentAt && (
                      <p className="text-[10px] text-brand-black/45 mt-0.5">{fmtDate(l.consentAt)}</p>
                    )}
                  </td>
                  <td>
                    <a
                      href={`mailto:${l.email}?subject=${encodeURIComponent("Following up on your SPIRUVA document request")}`}
                      data-testid={`download-lead-email-${l.email}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-green text-brand-cream text-[10px] font-mono uppercase tracking-widest hover:bg-brand-black transition"
                    >
                      <Mail size={11} /> Reply
                    </a>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Recent activity stream */}
      <div className="mt-8">
        <p className="eyebrow mb-3">◦  Recent activity</p>
        <div className="bg-white border border-brand-green/15 divide-y divide-brand-green/10">
          {recent.slice(0, 12).map((r) => (
            <div key={r.id} className="px-5 py-3 flex items-center justify-between gap-3 text-sm" data-testid={`download-activity-${r.id}`}>
              <div className="min-w-0 flex-1">
                <p className="text-brand-black truncate">
                  <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/45 mr-2">
                    {r.event === "verified_download" ? "Downloaded" : "Requested"}
                  </span>
                  <span className="font-medium">{r.resource_kind}/{r.resource_id}</span>
                  <span className="text-brand-black/55"> · {r.full_name || r.email}</span>
                </p>
                <p className="text-[11px] text-brand-black/45 mt-0.5">{fmtDate(r.createdAt)}</p>
              </div>
              <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 border ${
                r.event === "verified_download"
                  ? "border-brand-green/40 text-brand-green bg-brand-green/5"
                  : "border-brand-gold/40 text-[#8a6e1f] bg-brand-gold/5"
              }`}>{r.event === "verified_download" ? "verified" : "pending"}</span>
            </div>
          ))}
          {!loading && recent.length === 0 && (
            <p className="px-5 py-6 text-sm text-brand-black/50 text-center">No download activity yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Legacy blog (article) leads panel — original UI
// ----------------------------------------------------------------------------
const BlogLeadsPanel = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [country, setCountry] = useState("All");
  const [article, setArticle] = useState("All");
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminFetchLeads()
      .then((rows) => { if (alive) setLeads(rows || []); })
      .catch(() => toast.error("Could not load article leads"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const countries = useMemo(() => {
    const set = new Set();
    leads.forEach((l) => { if (l.country) set.add(l.country); });
    return ["All", ...Array.from(set).sort()];
  }, [leads]);

  const articles = useMemo(() => {
    const map = new Map();
    leads.forEach((l) => {
      if (l.source_slug) map.set(l.source_slug, l.source_title || l.source_slug);
    });
    return [["All", "All Articles"], ...Array.from(map.entries()).sort((a, b) => a[1].localeCompare(b[1]))];
  }, [leads]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    const list = leads.filter((l) => {
      if (country !== "All" && l.country !== country) return false;
      if (article !== "All" && l.source_slug !== article) return false;
      if (!term) return true;
      return (
        (l.email || "").toLowerCase().includes(term) ||
        (l.name || "").toLowerCase().includes(term) ||
        (l.company || "").toLowerCase().includes(term) ||
        (l.source_title || "").toLowerCase().includes(term)
      );
    });
    list.sort((a, b) => {
      const va = (a[sortKey] ?? "").toString();
      const vb = (b[sortKey] ?? "").toString();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    });
    return list;
  }, [leads, search, country, article, sortKey, sortDir]);

  const stats = useMemo(() => {
    const total = leads.length;
    const now = Date.now();
    const last30 = leads.filter((l) => {
      try { return now - new Date(l.createdAt).getTime() < 30 * 86400000; } catch { return false; }
    }).length;
    const uniqueCountries = new Set(leads.map((l) => l.country).filter(Boolean)).size;
    const articleCount = {};
    leads.forEach((l) => {
      if (l.source_slug) articleCount[l.source_slug] = (articleCount[l.source_slug] || 0) + 1;
    });
    let topArticle = ["", 0];
    Object.entries(articleCount).forEach(([k, v]) => { if (v > topArticle[1]) topArticle = [k, v]; });
    const topTitle = leads.find((l) => l.source_slug === topArticle[0])?.source_title || "—";
    return { total, last30, uniqueCountries, topTitle, topCount: topArticle[1] };
  }, [leads]);

  const toggleSort = (k) => {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("desc"); }
  };

  const exportCsv = () => {
    const csv = buildBlogCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `spiruva-article-leads-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported ${filtered.length} lead${filtered.length === 1 ? "" : "s"}`);
  };

  const copyEmail = async (id, email) => {
    try {
      await navigator.clipboard.writeText(email);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      toast.error("Could not copy email");
    }
  };

  const mailtoHref = (l) => {
    const subject = `Following up on your download — ${l.source_title || "Spiruva industry report"}`;
    const body =
      `Hi ${l.name || "there"},\n\n` +
      `Thank you for downloading "${l.source_title || "our report"}" from Spiruva.\n\n` +
      `Best regards,\nSpiruva Export Desk\nexport@spiruva.com  ·  spiruva.com`;
    return `mailto:${encodeURIComponent(l.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const clearFilters = () => { setSearch(""); setCountry("All"); setArticle("All"); };
  const hasFilter = search || country !== "All" || article !== "All";

  return (
    <div data-testid="admin-article-leads-panel">
      <div className="flex justify-end mb-4">
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="article-leads-export-csv"
          className="btn-primary disabled:opacity-50"
        >
          <Download size={14} /> Export CSV ({filtered.length})
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
        {[
          { icon: Users, l: "Total Leads", v: stats.total },
          { icon: TrendingUp, l: "Last 30 Days", v: stats.last30 },
          { icon: Globe2, l: "Unique Countries", v: stats.uniqueCountries },
          { icon: FileText, l: "Top Article", v: stats.topCount > 0 ? `${stats.topCount} downloads` : "—", sub: stats.topTitle },
        ].map((s) => (
          <div key={s.l} className="bg-white p-6">
            <s.icon size={14} className="text-brand-teal" />
            <p className="font-display text-3xl text-brand-black mt-3">{s.v}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
            {s.sub && <p className="text-[11px] text-brand-black/55 mt-2 line-clamp-1" title={s.sub}>{s.sub}</p>}
          </div>
        ))}
      </div>

      <div className="bg-white border border-brand-green/15 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search size={14} className="text-brand-black/40 shrink-0" />
          <input
            type="text"
            placeholder="Search by name, email, company, article…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="leads-search"
            className="flex-1 bg-transparent text-sm focus:outline-none min-w-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-brand-black/40" />
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            data-testid="leads-filter-country"
            className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {countries.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select
            value={article}
            onChange={(e) => setArticle(e.target.value)}
            data-testid="leads-filter-article"
            className="max-w-[260px] px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {articles.map(([slug, label]) => (
              <option key={slug} value={slug}>{label}</option>
            ))}
          </select>
          {hasFilter && (
            <button
              onClick={clearFilters}
              data-testid="leads-clear-filters"
              className="text-xs font-mono uppercase tracking-widest text-brand-black/60 hover:text-brand-gold inline-flex items-center gap-1"
            >
              <X size={12} /> Clear
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border border-brand-green/15 overflow-x-auto">
        <table className="clinical-table">
          <thead>
            <tr>
              <th onClick={() => toggleSort("createdAt")} className="cursor-pointer">Date</th>
              <th onClick={() => toggleSort("name")} className="cursor-pointer">Contact</th>
              <th onClick={() => toggleSort("company")} className="cursor-pointer">Company</th>
              <th onClick={() => toggleSort("country")} className="cursor-pointer">Country</th>
              <th>Article</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center text-brand-black/50" data-testid="leads-loading">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={6} className="text-center text-brand-black/50 py-10" data-testid="leads-empty">
                {leads.length === 0
                  ? "No article leads captured yet."
                  : "No leads match your filters."}
              </td></tr>
            ) : filtered.map((l) => (
              <tr key={l.id} data-testid={`lead-row-${l.id}`}>
                <td>
                  <p className="text-xs text-brand-black/80">{fmtDay(l.createdAt)}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">{fmtDate(l.createdAt).split(", ").pop()}</p>
                </td>
                <td>
                  <p className="font-medium text-brand-black text-sm">{l.name || "—"}</p>
                  <button
                    onClick={() => copyEmail(l.id, l.email)}
                    className="inline-flex items-center gap-1.5 text-xs text-brand-teal hover:text-brand-green font-mono mt-0.5"
                    title="Copy email"
                  >
                    {l.email}
                    {copiedId === l.id ? <Check size={11} className="text-brand-green" /> : <Copy size={10} className="opacity-50" />}
                  </button>
                </td>
                <td className="max-w-[180px]">
                  <p className="font-medium text-brand-black text-sm line-clamp-2">{l.company}</p>
                </td>
                <td>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/25 text-brand-green bg-brand-green/5">
                    {l.country}
                  </span>
                </td>
                <td className="max-w-[260px]">
                  {l.source_slug ? (
                    <a
                      href={`/blog/${l.source_slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-brand-black/85 hover:text-brand-teal line-clamp-2"
                      title={l.source_title}
                    >
                      {l.source_title || l.source_slug}
                    </a>
                  ) : "—"}
                </td>
                <td>
                  <a
                    href={mailtoHref(l)}
                    data-testid={`lead-email-${l.id}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-green text-brand-cream text-[10px] font-mono uppercase tracking-widest hover:bg-brand-black transition"
                  >
                    <Mail size={11} /> Email Lead
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Newsletter subscribers panel
// ----------------------------------------------------------------------------
const NewsletterPanel = () => {
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState([]);
  const [search, setSearch] = useState("");
  const [localeFilter, setLocaleFilter] = useState("All");
  const [sourceFilter, setSourceFilter] = useState("All");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    adminFetchNewsletterSubscribers()
      .then((data) => { if (alive) setSubs(data?.subscribers || []); })
      .catch(() => toast.error("Could not load newsletter subscribers"))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const sources = useMemo(() => {
    const set = new Set();
    subs.forEach((s) => { if (s.source) set.add(s.source); });
    return ["All", ...Array.from(set).sort()];
  }, [subs]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return subs.filter((s) => {
      if (localeFilter !== "All" && (s.locale || "en") !== localeFilter) return false;
      if (sourceFilter !== "All" && s.source !== sourceFilter) return false;
      if (!term) return true;
      return (s.email || "").toLowerCase().includes(term) || (s.name || "").toLowerCase().includes(term);
    }).sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime());
  }, [subs, search, localeFilter, sourceFilter]);

  const stats = useMemo(() => {
    const now = Date.now();
    const last7 = subs.filter((s) => {
      try { return now - new Date(s.createdAt).getTime() < 7 * 86400000; } catch { return false; }
    }).length;
    const last30 = subs.filter((s) => {
      try { return now - new Date(s.createdAt).getTime() < 30 * 86400000; } catch { return false; }
    }).length;
    const exitIntent = subs.filter((s) => s.source === "exit-intent").length;
    return { total: subs.length, last7, last30, exitIntent };
  }, [subs]);

  const exportCsv = () => {
    const csv = buildSubscribersCsv(filtered);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spiruva-newsletter-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 500);
    toast.success(`Exported ${filtered.length} subscriber${filtered.length === 1 ? "" : "s"}`);
  };

  return (
    <div data-testid="admin-newsletter-panel">
      <div className="flex justify-end mb-4">
        <button
          onClick={exportCsv}
          disabled={filtered.length === 0}
          data-testid="newsletter-export-csv"
          className="btn-primary disabled:opacity-50"
        >
          <Download size={14} /> Export CSV ({filtered.length})
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-brand-green/15 border border-brand-green/15 mb-8">
        {[
          { icon: Users, l: "Total subscribers", v: stats.total },
          { icon: TrendingUp, l: "Last 7 days", v: stats.last7 },
          { icon: TrendingUp, l: "Last 30 days", v: stats.last30 },
          { icon: Sparkles, l: "Exit-intent capture", v: stats.exitIntent },
        ].map((s) => (
          <div key={s.l} className="bg-white p-6">
            <s.icon size={14} className="text-brand-teal" />
            <p className="font-display text-3xl text-brand-black mt-3">{s.v}</p>
            <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/50 mt-1">{s.l}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-brand-green/15 p-4 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <Search size={14} className="text-brand-black/40 shrink-0" />
          <input
            type="text"
            placeholder="Search email or name…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="newsletter-admin-search"
            className="flex-1 bg-transparent text-sm focus:outline-none min-w-0"
          />
        </div>
        <div className="flex items-center gap-2">
          <Filter size={13} className="text-brand-black/40" />
          <select
            value={localeFilter}
            onChange={(e) => setLocaleFilter(e.target.value)}
            data-testid="newsletter-admin-filter-locale"
            className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {["All", "en", "de", "ja"].map((l) => <option key={l} value={l}>{l}</option>)}
          </select>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            data-testid="newsletter-admin-filter-source"
            className="px-3 py-1.5 bg-brand-cream/40 border border-brand-green/20 text-xs font-mono uppercase tracking-widest focus:outline-none"
          >
            {sources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>
      </div>

      <div className="bg-white border border-brand-green/15 overflow-x-auto">
        <table className="clinical-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Email</th>
              <th>Name</th>
              <th>Locale</th>
              <th>Source</th>
              <th>Consent</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-brand-black/50 py-8" data-testid="newsletter-admin-loading">Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-brand-black/50 py-10" data-testid="newsletter-admin-empty">
                {subs.length === 0
                  ? "No newsletter subscribers yet. Visitors who opt in via the exit-intent modal will appear here."
                  : "No subscribers match your filters."}
              </td></tr>
            ) : filtered.map((s) => (
              <tr key={s.id || s.email} data-testid={`newsletter-row-${s.email}`}>
                <td>
                  <p className="text-xs text-brand-black/85">{fmtDay(s.createdAt)}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-black/40 mt-0.5">{fmtDate(s.createdAt).split(", ").pop()}</p>
                </td>
                <td>
                  <p className="font-mono text-xs text-brand-teal">{s.email}</p>
                </td>
                <td>
                  <p className="text-sm text-brand-black/85">{s.name || "—"}</p>
                </td>
                <td>
                  <span className="font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/25 text-brand-green bg-brand-green/5">
                    {(s.locale || "en").toUpperCase()}
                  </span>
                </td>
                <td>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-brand-black/65">{s.source || "—"}</span>
                </td>
                <td>
                  {s.consent ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-brand-green">
                      <Check size={11} /> Yes
                    </span>
                  ) : "—"}
                  {s.consentAt && <p className="text-[10px] text-brand-black/45 mt-0.5">{fmtDate(s.consentAt)}</p>}
                </td>
                <td>
                  <span className="inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest px-2 py-1 border border-brand-green/40 text-brand-green bg-brand-green/10">
                    <ShieldCheck size={11} /> {s.status || "subscribed"}
                  </span>
                </td>
                <td>
                  <a
                    href={`mailto:${s.email}`}
                    data-testid={`newsletter-email-${s.email}`}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-brand-green text-brand-cream text-[10px] font-mono uppercase tracking-widest hover:bg-brand-black transition"
                  >
                    <Mail size={11} /> Email
                  </a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminLeads;

// ----------------------------------------------------------------------------
// Campaigns panel — AI-generated weekly newsletter drafts (EN/DE/JA)
// ----------------------------------------------------------------------------
const CAMPAIGN_STATUS = {
  draft:     { label: "Draft",     className: "border-brand-gold/40 text-[#8a6e1f] bg-brand-gold/10" },
  approved:  { label: "Approved",  className: "border-brand-teal/40 text-brand-teal bg-brand-teal/10" },
  scheduled: { label: "Scheduled", className: "border-brand-teal/40 text-brand-teal bg-brand-teal/10" },
  sent:      { label: "Sent",      className: "border-brand-green/40 text-brand-green bg-brand-green/10" },
  cancelled: { label: "Cancelled", className: "border-brand-black/20 text-brand-black/55 bg-brand-black/5" },
};

const LOCALE_TABS = ["en", "de", "ja"];

const CampaignsPanel = () => {
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [localeTab, setLocaleTab] = useState("en");
  const [confirm, setConfirm] = useState(null); // { action: 'approve'|'cancel', id }

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminFetchCampaigns();
      setCampaigns(data?.campaigns || []);
    } catch (e) {
      toast.error("Could not load campaigns");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const onGenerate = async () => {
    setBusy(true);
    try {
      const data = await adminGenerateCampaign();
      toast.success(`Draft generated · ${data?.campaign?.id?.slice(0, 8) || "ok"}`);
      await load();
      setOpenId(data?.campaign?.id || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const onApprove = async (id, schedule) => {
    setBusy(true);
    try {
      const data = await adminApproveCampaign(id, schedule);
      if (data?.scheduled) {
        toast.success(`Scheduled for ${new Date(data.scheduledFor).toLocaleString()}`);
      } else {
        const r = data?.result || {};
        toast.success(`Sent ${r.sent ?? 0} · failed ${r.failed ?? 0}`);
      }
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(false);
    }
  };

  const onUnschedule = async (id) => {
    setBusy(true);
    try {
      await adminUnscheduleCampaign(id);
      toast.success("Unscheduled — campaign returned to draft");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Unschedule failed");
    } finally {
      setBusy(false);
    }
  };

  const onCancel = async (id) => {
    setBusy(true);
    try {
      await adminCancelCampaign(id);
      toast.success("Campaign cancelled");
      setConfirm(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    } finally {
      setBusy(false);
    }
  };

  const stats = useMemo(() => {
    const drafts = campaigns.filter((c) => c.status === "draft").length;
    const sent = campaigns.filter((c) => c.status === "sent").length;
    const lastSent = campaigns.find((c) => c.status === "sent")?.sentAt || null;
    const totalRecipients = campaigns
      .filter((c) => c.status === "sent")
      .reduce((acc, c) => acc + (c?.recipient_counts?.total || 0), 0);
    return { drafts, sent, lastSent, totalRecipients, total: campaigns.length };
  }, [campaigns]);

  const open = campaigns.find((c) => c.id === openId) || null;

  return (
    <div data-testid="campaigns-panel">
      {/* Header / actions */}
      <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between mb-6">
        <div>
          <p className="text-xs font-mono uppercase tracking-[0.18em] text-brand-black/55">
            Weekly AI Newsletter · EN · DE · JA
          </p>
          <p className="text-sm text-brand-black/65 mt-1 max-w-xl">
            Auto-drafted each Tuesday 09:00 UTC from the 8 most recent published Journal posts. Review per-locale, then approve to send to opted-in subscribers with one-click unsubscribe.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            data-testid="campaigns-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 border border-brand-green/25 text-xs font-mono uppercase tracking-[0.18em] hover:border-brand-teal/50 hover:bg-brand-teal/5 disabled:opacity-40"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
          <button
            type="button"
            onClick={onGenerate}
            disabled={busy}
            data-testid="campaigns-generate"
            className="inline-flex items-center gap-2 px-4 py-2 bg-brand-green text-brand-cream text-xs font-mono uppercase tracking-[0.18em] hover:bg-brand-green/90 disabled:opacity-40"
          >
            <Sparkles size={13} /> {busy ? "Generating…" : "Generate Draft"}
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
        <StatTile label="Total" value={stats.total} Icon={Megaphone} />
        <StatTile label="Drafts" value={stats.drafts} Icon={FileText} accent="gold" />
        <StatTile label="Sent" value={stats.sent} Icon={Send} accent="green" />
        <StatTile label="Recipients (all)" value={stats.totalRecipients} Icon={Users} accent="teal" />
      </div>

      {/* Empty state */}
      {!loading && campaigns.length === 0 && (
        <div className="text-center py-16 border border-dashed border-brand-green/30 bg-brand-cream/30">
          <Megaphone className="mx-auto mb-3 text-brand-black/40" size={32} />
          <p className="text-sm text-brand-black/70">No campaigns yet. Click <strong>Generate Draft</strong> to create the first one.</p>
        </div>
      )}

      {/* List */}
      {campaigns.length > 0 && (
        <div className="border border-brand-green/20 bg-white overflow-hidden">
          <table className="w-full text-sm" data-testid="campaigns-table">
            <thead>
              <tr className="bg-brand-cream/60 text-left text-[11px] font-mono uppercase tracking-[0.14em] text-brand-black/55">
                <th className="px-4 py-3 w-1/2">Campaign</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => {
                const s = CAMPAIGN_STATUS[c.status] || CAMPAIGN_STATUS.draft;
                const enSubject = c?.drafts?.en?.subject || "(no subject)";
                const issues = Object.entries(c?.safety_issues || {}).filter(([, v]) => v);
                return (
                  <tr key={c.id} className="border-t border-brand-green/10 hover:bg-brand-cream/30" data-testid={`campaign-row-${c.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-brand-black truncate max-w-[480px]">{enSubject}</div>
                      <div className="text-[11px] font-mono text-brand-black/45 mt-0.5">
                        id <code>{c.id.slice(0, 8)}</code>
                        {issues.length > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[#b34d4d]">
                            <AlertTriangle size={10} /> {issues.length} safety issue{issues.length === 1 ? "" : "s"}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.16em] border ${s.className}`}>
                        {s.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-xs text-brand-black/60 whitespace-nowrap">{fmtDate(c.createdAt)}</td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => { setOpenId(c.id); setLocaleTab("en"); }}
                        data-testid={`campaign-preview-${c.id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] border border-brand-green/25 hover:border-brand-teal/50 hover:bg-brand-teal/5"
                      >
                        <Eye size={11} /> Preview
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Preview drawer */}
      {open && (
        <div className="fixed inset-0 z-50 flex items-stretch justify-end" data-testid="campaign-drawer">
          <div className="absolute inset-0 bg-brand-black/40" onClick={() => setOpenId(null)} />
          <div className="relative w-full max-w-3xl h-full bg-white shadow-2xl overflow-y-auto">
            <div className="sticky top-0 z-10 bg-white border-b border-brand-green/15 px-6 py-4 flex items-start justify-between gap-4">
              <div>
                <p className="text-[11px] font-mono uppercase tracking-[0.18em] text-brand-black/55">
                  Campaign <code>{open.id.slice(0, 8)}</code> · {fmtDate(open.createdAt)}
                </p>
                <h3 className="font-display text-2xl text-brand-black mt-1 leading-tight">
                  {open?.drafts?.[localeTab]?.subject || "(no subject)"}
                </h3>
                <p className="text-xs text-brand-black/55 mt-1">{open?.drafts?.[localeTab]?.preheader || ""}</p>
              </div>
              <button
                type="button"
                onClick={() => setOpenId(null)}
                data-testid="campaign-drawer-close"
                className="p-1.5 hover:bg-brand-cream/60"
              >
                <X size={18} />
              </button>
            </div>

            {/* Locale tabs */}
            <div className="px-6 pt-4 flex gap-1 border-b border-brand-green/10">
              {LOCALE_TABS.map((lc) => {
                const issue = (open?.safety_issues || {})[lc];
                return (
                  <button
                    key={lc}
                    type="button"
                    onClick={() => setLocaleTab(lc)}
                    data-testid={`campaign-locale-${lc}`}
                    className={`px-4 py-2 text-xs font-mono uppercase tracking-[0.18em] border-b-2 -mb-px transition ${
                      localeTab === lc
                        ? "border-brand-gold text-brand-black"
                        : "border-transparent text-brand-black/50 hover:text-brand-black"
                    }`}
                  >
                    {lc}
                    {issue && <AlertTriangle size={10} className="inline ml-1.5 text-[#b34d4d]" />}
                  </button>
                );
              })}
            </div>

            <div className="p-6">
              {/* Safety flag */}
              {(open?.safety_issues || {})[localeTab] && (
                <div className="mb-4 px-4 py-3 border border-[#e6b8b8] bg-[#fcf3f3] text-[#7a2e2e] text-xs flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
                  <div>
                    <strong className="block">Safety flag</strong>
                    <span className="font-mono">{(open?.safety_issues || {})[localeTab]}</span>
                    <span className="block mt-1">Approving will cause this locale to be skipped for affected sends.</span>
                  </div>
                </div>
              )}

              {/* Source post slugs */}
              {(open?.source_post_slugs || []).length > 0 && (
                <div className="mb-4 text-[11px] font-mono uppercase tracking-[0.14em] text-brand-black/55">
                  Sourced from:{" "}
                  {(open.source_post_slugs || []).map((slug, i) => (
                    <span key={slug}>
                      <code className="text-brand-teal normal-case">{slug}</code>
                      {i < (open.source_post_slugs.length - 1) ? " · " : ""}
                    </span>
                  ))}
                </div>
              )}

              {/* HTML preview */}
              <div className="border border-brand-green/15 bg-brand-cream/30 mb-6" data-testid="campaign-html-preview">
                <iframe
                  title="campaign-preview"
                  srcDoc={`<!doctype html><html><head><meta charset="utf-8"/><style>body{margin:0;font-family:Arial,sans-serif;background:#f8f5ef;color:#0f1f14;line-height:1.6;padding:20px;}p{margin:0 0 14px;}a{color:#006d77;}</style></head><body>${open?.drafts?.[localeTab]?.html_body || "<p>(empty)</p>"}</body></html>`}
                  className="w-full h-[420px] bg-white"
                  sandbox=""
                />
              </div>

              {/* Plain text */}
              <details className="mb-6 text-xs">
                <summary className="cursor-pointer text-brand-black/65 font-mono uppercase tracking-[0.14em]">Plain-text variant</summary>
                <pre className="mt-2 p-4 border border-brand-green/15 bg-brand-cream/30 text-brand-black/75 whitespace-pre-wrap font-mono text-[12px]">
{open?.drafts?.[localeTab]?.plain_body || "(empty)"}
                </pre>
              </details>

              {/* Recipient counts (if sent) */}
              {open?.status === "sent" && (
                <div className="mb-6 p-4 border border-brand-green/15 bg-brand-cream/30">
                  <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-brand-black/55 mb-2">Send result</p>
                  <p className="text-sm text-brand-black/80">
                    Sent <strong>{open?.send_results?.sent ?? 0}</strong> · failed <strong>{open?.send_results?.failed ?? 0}</strong> · checked <strong>{open?.send_results?.subscribers_checked ?? 0}</strong> · {fmtDate(open?.sentAt)}
                  </p>
                </div>
              )}

              {/* Scheduled info */}
              {open?.status === "scheduled" && open?.scheduledFor && (
                <div className="mb-6 p-4 border border-brand-teal/30 bg-brand-teal/5 flex items-start gap-3" data-testid="campaign-scheduled-banner">
                  <CalendarClock size={18} className="text-brand-teal mt-0.5 flex-shrink-0" />
                  <div className="flex-1">
                    <p className="text-[11px] font-mono uppercase tracking-[0.14em] text-brand-teal mb-1">Scheduled</p>
                    <p className="text-sm text-brand-black/85">
                      Will send at <strong>{new Date(open.scheduledFor).toLocaleString()}</strong>
                      {open?.scheduledTimezone ? ` (${open.scheduledTimezone})` : ""}.
                    </p>
                    <p className="text-xs text-brand-black/55 mt-1">Approved by {open?.approvedBy || "admin"} at {fmtDate(open?.approvedAt)}.</p>
                  </div>
                </div>
              )}

              {/* Actions */}
              {open?.status === "draft" && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-brand-green/10 mt-4">
                  <button
                    type="button"
                    onClick={() => setConfirm({ action: "approve", id: open.id })}
                    disabled={busy}
                    data-testid="campaign-approve-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-brand-teal text-brand-cream text-xs font-mono uppercase tracking-[0.18em] hover:bg-brand-teal/90 disabled:opacity-40"
                  >
                    <Send size={13} /> Approve & Send
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirm({ action: "cancel", id: open.id })}
                    disabled={busy}
                    data-testid="campaign-cancel-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-brand-black/20 text-brand-black/70 text-xs font-mono uppercase tracking-[0.18em] hover:bg-brand-black/5 disabled:opacity-40"
                  >
                    <Ban size={13} /> Cancel Campaign
                  </button>
                </div>
              )}
              {open?.status === "scheduled" && (
                <div className="flex flex-wrap gap-2 pt-2 border-t border-brand-green/10 mt-4">
                  <button
                    type="button"
                    onClick={() => onUnschedule(open.id)}
                    disabled={busy}
                    data-testid="campaign-unschedule-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-brand-black/20 text-brand-black/70 text-xs font-mono uppercase tracking-[0.18em] hover:bg-brand-black/5 disabled:opacity-40"
                  >
                    <Ban size={13} /> Unschedule (return to draft)
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm modal */}
      {confirm && (
        <ApproveOrCancelModal
          confirm={confirm}
          busy={busy}
          onClose={() => !busy && setConfirm(null)}
          onApproveNow={(id) => onApprove(id)}
          onApproveSchedule={(id, sched) => onApprove(id, sched)}
          onCancel={(id) => onCancel(id)}
        />
      )}
    </div>
  );
};

// Wrapper so the confirm modal can call onCancel without name-clashing the panel-level onCancel
const _bindCancel = (fn) => fn;

// Scheduling defaults / helpers --------------------------------------------
const TIMEZONE_OPTIONS = [
  { value: "Asia/Tokyo",     label: "JST (UTC+9) · Tokyo"           },
  { value: "Europe/Berlin",  label: "CET (UTC+1/+2) · Berlin"       },
  { value: "Europe/London",  label: "GMT (UTC+0/+1) · London"       },
  { value: "America/New_York", label: "ET (UTC-5/-4) · New York"    },
  { value: "UTC",            label: "UTC"                           },
];

// Next Wednesday 10:00 in the chosen IANA zone (returns ISO string with offset)
const nextWedTenLocal = (tzValue) => {
  // We compose a Date in the user's *local* clock, then re-derive a wall-time
  // string that the backend can interpret with the chosen offset. The simplest
  // robust path: use Intl APIs to find what "Wed 10:00 in tzValue" looks like
  // in the user's local browser time, then export to datetime-local string.
  // For simplicity here, we just produce the next-Wed 10:00 in the *browser*
  // local time and let the user adjust via the timezone select for the BACKEND
  // interpretation. The backend ALWAYS treats the input as ISO with offset.
  const d = new Date();
  const day = d.getDay(); // 0=Sun..6=Sat — Wed=3
  let add = (3 - day + 7) % 7;
  if (add === 0 && (d.getHours() >= 10)) add = 7;
  d.setDate(d.getDate() + add);
  d.setHours(10, 0, 0, 0);
  // Return as a datetime-local-friendly string (no offset)
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

// Compose ISO-8601 with offset for the chosen timezone
const composeIsoWithOffset = (localDateTimeString, tzValue) => {
  // localDateTimeString comes from <input type="datetime-local"> — has no offset.
  // We construct a Date in the user's browser timezone, then convert to ISO
  // with the chosen IANA zone's offset.
  if (!localDateTimeString) return null;
  try {
    // Parse the local components literally
    const [datePart, timePart] = localDateTimeString.split("T");
    const [Y, M, D] = datePart.split("-").map(Number);
    const [h, m] = (timePart || "00:00").split(":").map(Number);
    // Build a Date with those wall-clock values as if they were UTC
    const utcGuess = new Date(Date.UTC(Y, M - 1, D, h, m));
    // Now compute the offset (in minutes) of tzValue at that instant
    const fmt = new Intl.DateTimeFormat("en-US", {
      timeZone: tzValue,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
    });
    const parts = fmt.formatToParts(utcGuess).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
    const tzClock = Date.UTC(
      Number(parts.year), Number(parts.month) - 1, Number(parts.day),
      Number(parts.hour) === 24 ? 0 : Number(parts.hour), Number(parts.minute), Number(parts.second)
    );
    const offsetMs = tzClock - utcGuess.getTime(); // tz - utc, both seen at the same instant
    // The instant we WANT (wall-clock in tzValue) is utcGuess - offsetMs
    const wantedInstantMs = utcGuess.getTime() - offsetMs;
    const wanted = new Date(wantedInstantMs);
    // Format with explicit offset
    const offMin = -offsetMs / 60000; // minutes EAST of UTC for tzValue at that wallclock
    const sign = offMin >= 0 ? "+" : "-";
    const abs = Math.abs(offMin);
    const oh = String(Math.floor(abs / 60)).padStart(2, "0");
    const om = String(Math.floor(abs % 60)).padStart(2, "0");
    return wanted.toISOString().replace(/\.\d{3}Z$/, "") + sign + oh + ":" + om;
  } catch {
    return null;
  }
};

const ApproveOrCancelModal = ({ confirm, busy, onClose, onApproveNow, onApproveSchedule, onCancel }) => {
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const [tz, setTz] = useState("Asia/Tokyo");
  const [when, setWhen] = useState(() => nextWedTenLocal("Asia/Tokyo"));

  if (confirm.action === "cancel") {
    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="campaign-confirm">
        <div className="absolute inset-0 bg-brand-black/55" onClick={onClose} />
        <div className="relative bg-white max-w-md w-full p-6 shadow-2xl">
          <h4 className="font-display text-xl text-brand-black mb-2">Cancel this draft?</h4>
          <p className="text-sm text-brand-black/70 mb-6">
            Cancelling marks the draft as discarded. You can always regenerate.
          </p>
          <div className="flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              data-testid="campaign-confirm-no"
              className="px-4 py-2 border border-brand-black/20 text-xs font-mono uppercase tracking-[0.16em] hover:bg-brand-black/5 disabled:opacity-40"
            >
              Back
            </button>
            <button
              type="button"
              onClick={() => onCancel(confirm.id)}
              disabled={busy}
              data-testid="campaign-confirm-yes"
              className="px-4 py-2 bg-brand-black text-brand-cream text-xs font-mono uppercase tracking-[0.16em] hover:bg-brand-black/85 disabled:opacity-40"
            >
              {busy ? "Working…" : "Confirm cancel"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const scheduleIso = mode === "schedule" ? composeIsoWithOffset(when, tz) : null;
  const canSubmit = mode === "now" || (scheduleIso && new Date(scheduleIso).getTime() > Date.now() + 60_000);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" data-testid="campaign-confirm">
      <div className="absolute inset-0 bg-brand-black/55" onClick={onClose} />
      <div className="relative bg-white max-w-lg w-full p-6 shadow-2xl">
        <h4 className="font-display text-xl text-brand-black mb-1">Send this campaign?</h4>
        <p className="text-sm text-brand-black/65 mb-5">
          The EN / DE / JA drafts will be matched to each opted-in subscriber by their locale. Locales with safety flags are skipped per-recipient.
        </p>

        {/* Mode toggle */}
        <div className="flex gap-2 mb-4">
          <button
            type="button"
            onClick={() => setMode("now")}
            data-testid="schedule-mode-now"
            className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-[0.16em] border transition ${
              mode === "now"
                ? "border-brand-teal bg-brand-teal/10 text-brand-teal"
                : "border-brand-black/15 text-brand-black/55 hover:border-brand-black/30"
            }`}
          >
            <Send size={12} className="inline mr-2" /> Send now
          </button>
          <button
            type="button"
            onClick={() => setMode("schedule")}
            data-testid="schedule-mode-later"
            className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-[0.16em] border transition ${
              mode === "schedule"
                ? "border-brand-teal bg-brand-teal/10 text-brand-teal"
                : "border-brand-black/15 text-brand-black/55 hover:border-brand-black/30"
            }`}
          >
            <CalendarClock size={12} className="inline mr-2" /> Schedule
          </button>
        </div>

        {mode === "schedule" && (
          <div className="space-y-3 p-4 border border-brand-green/15 bg-brand-cream/30 mb-5" data-testid="schedule-fields">
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-brand-black/55 mb-1">Date & time (local clock)</label>
              <input
                type="datetime-local"
                value={when}
                onChange={(e) => setWhen(e.target.value)}
                data-testid="schedule-datetime"
                className="w-full px-3 py-2 border border-brand-green/25 bg-white text-sm font-mono"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-[0.18em] text-brand-black/55 mb-1">Timezone (interpretation)</label>
              <select
                value={tz}
                onChange={(e) => setTz(e.target.value)}
                data-testid="schedule-timezone"
                className="w-full px-3 py-2 border border-brand-green/25 bg-white text-sm font-mono"
              >
                {TIMEZONE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <p className="text-[11px] text-brand-black/60 leading-relaxed">
              Recommended: <strong>Wed 10:00 JST</strong> — Tokyo opens, then EU mid-morning, then US mid-afternoon. Strong open-rate window across all three target regions.
            </p>
            {scheduleIso && (
              <p className="text-[11px] font-mono text-brand-teal" data-testid="schedule-iso-preview">
                Will dispatch at: {new Date(scheduleIso).toLocaleString()} ({tz})
              </p>
            )}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            data-testid="campaign-confirm-no"
            className="px-4 py-2 border border-brand-black/20 text-xs font-mono uppercase tracking-[0.16em] hover:bg-brand-black/5 disabled:opacity-40"
          >
            Back
          </button>
          <button
            type="button"
            onClick={() => {
              if (mode === "now") return onApproveNow(confirm.id);
              return onApproveSchedule(confirm.id, { run_at: scheduleIso, timezone: tz });
            }}
            disabled={busy || !canSubmit}
            data-testid="campaign-confirm-yes"
            className="px-4 py-2 text-xs font-mono uppercase tracking-[0.16em] disabled:opacity-40 bg-brand-teal text-brand-cream hover:bg-brand-teal/90"
          >
            {busy ? "Working…" : mode === "now" ? "Send now" : "Schedule send"}
          </button>
        </div>
      </div>
    </div>
  );
};

const StatTile = ({ label, value, Icon, accent }) => {
  const accentClass = accent === "gold"
    ? "border-brand-gold/40"
    : accent === "green"
    ? "border-brand-green/30"
    : accent === "teal"
    ? "border-brand-teal/30"
    : "border-brand-green/15";
  return (
    <div className={`p-4 border bg-white ${accentClass}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.18em] text-brand-black/55">
        <Icon size={12} /> {label}
      </div>
      <div className="font-display text-3xl text-brand-black mt-1">{value}</div>
    </div>
  );
};